import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      console.log('Stock Out API: Token obtained successfully');
      return idToken;
    }
    
    console.warn('Stock Out API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Stock Out API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Stock Out API: Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `Stock Out API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );
      
      // Special handling for path parameter errors
      if (response.status === 400 && (errorData.error?.includes('Path parameter') || errorData.message?.includes('Path parameter'))) {
        console.error('Stock Out API: ⚠️ Path parameter error detected');
        console.error('Stock Out API: Error details:', errorData);
        console.error('Stock Out API: This usually means the API Gateway route is not configured correctly');
        console.error('Stock Out API: The route should have a path parameter like {id} or {ledger_id}');
        console.error('Stock Out API: Current endpoint:', endpoint);
        console.error('Stock Out API: Full URL:', `${API_BASE_URL}${endpoint}`);
        console.error('Stock Out API: Expected format: PUT /stock/stock-out/{id} or PUT /stock/stock-out/{ledger_id}');
        console.error('Stock Out API: Backend needs to configure API Gateway route to extract path parameter');
        console.error('Stock Out API: The Lambda function should receive event.pathParameters.id or event.pathParameters.ledger_id');
      }

      const error = new Error(
        `API Error: ${response.status} - ${errorData.error || errorData.message || 'Unknown error'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;

      // Log API error to logging service
      const responseText = JSON.stringify(errorData);
      loggingService.logError(
        'api_error_stock_out',
        `Stock Out API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: responseText,
        }
      ).catch(logError => {
        console.warn('[stockOutApi] Failed to log error:', logError);
      });

      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      // Log authentication error to logging service
      loggingService.logError(
        'api_error_stock_out',
        `Stock Out API authentication error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: 'AuthenticationError',
        }
      ).catch(logError => {
        console.warn('[stockOutApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    console.error(`Stock Out API: Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;

    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_stock_out',
      `Stock Out API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[stockOutApi] Failed to log error:', logError);
    });

    throw error;
  }
};

/**
 * API utility functions for Stock Out Management.
 */
export const stockOutApi = {
  /**
   * Fetches stock items for selection.
   * GET /stock/items
   * @param {Object} params - Optional parameters
   * @param {string} [params.siteId] - Filter by site ID
   * @param {number} [params.limit] - Maximum items to return
   * @returns {Promise<Object>} Object containing success status and data
   */
  getStockItems: async ({ siteId, limit } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (siteId) {
        queryParams.append('site_id', siteId);
      }
      if (limit) {
        queryParams.append('limit', limit.toString());
      }

      const endpoint = `/stock/items${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      return {
        success: true,
        data: responseData,
        items: responseData.items || [],
      };
    } catch (error) {
      console.error('Stock Out API: Error in getStockItems:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          items: [],
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message: error.responseData?.message || error.message || 'Failed to fetch stock items',
        items: [],
      };
    }
  },

  /**
   * Creates a stock-out transaction.
   * POST /stock/stock-out
   * @param {Object} stockOutData - Stock-out data
   * @param {string} [stockOutData.stock_date] - Stock date (YYYY-MM-DD, default: today)
   * @param {string} [stockOutData.reference_id] - Reference ID (optional)
   * @param {Array} stockOutData.lines - Array of stock-out line items
   * @param {string} lines[].stock_item_id - Stock item ID (required)
   * @param {number} lines[].quantity - Quantity (required, must be > 0)
   * @param {string} lines[].reason - Reason: "Sold" or "Returned" (required)
   * @param {number} [lines[].selling_price] - Selling price (required when reason = "Sold")
   * @param {string} [lines[].vendor_name] - Vendor name (optional)
   * @param {string} [lines[].expiry_date] - Expiry date (optional)
   * @param {string} [lines[].notes] - Notes (optional)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createStockOut: async (stockOutData) => {
    try {
      console.log('📤 Stock Out API: Creating stock-out with payload:', stockOutData);
      const response = await makeApiRequest('/stock/stock-out', 'POST', stockOutData);
      const responseData = await response.json();

      console.log('📥 Stock Out API: Create response:', responseData);
      console.log('📥 Stock Out API: Response keys:', Object.keys(responseData));

      // Try multiple possible response structures
      let entries = [];
      if (responseData.entries) {
        entries = responseData.entries;
        console.log('✅ Found entries in responseData.entries');
      } else if (responseData.stock_out_entries) {
        entries = responseData.stock_out_entries;
        console.log('✅ Found entries in responseData.stock_out_entries');
      } else if (Array.isArray(responseData)) {
        entries = responseData;
        console.log('✅ Response is direct array');
      } else if (responseData.data && Array.isArray(responseData.data)) {
        entries = responseData.data;
        console.log('✅ Found entries in responseData.data');
      } else {
        console.warn('⚠️ No entries found in expected locations. Full response:', JSON.stringify(responseData, null, 2));
      }

      console.log('📊 Stock Out API: Parsed entries:', entries);

      return {
        success: true,
        data: responseData,
        entries: entries,
      };
    } catch (error) {
      console.error('Stock Out API: Error in createStockOut:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message: error.responseData?.message || error.message || 'Failed to create stock-out',
      };
    }
  },

  /**
   * Lists stock-out entries with optional filtering.
   * GET /stock/stock-out
   * @param {Object} params - Optional parameters
   * @param {string} [params.siteId] - Filter by site ID
   * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
   * @param {string} [params.endDate] - End date (YYYY-MM-DD)
   * @param {string} [params.month] - Month filter (YYYY-MM format)
   * @param {number} [params.limit] - Page size (default: 50, max: 100)
   * @param {string} [params.lastKey] - Pagination token
   * @returns {Promise<Object>} Object containing success status and data
   */
  getStockOutEntries: async ({ siteId, startDate, endDate, month, limit, lastKey } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (siteId) {
        queryParams.append('site_id', siteId);
      }
      if (startDate) {
        queryParams.append('start_date', startDate);
      }
      if (endDate) {
        queryParams.append('end_date', endDate);
      }
      if (month) {
        queryParams.append('month', month);
      }
      if (limit) {
        queryParams.append('limit', limit.toString());
      }
      if (lastKey) {
        queryParams.append('last_key', lastKey);
      }

      const endpoint = `/stock/stock-out${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('🔍 Stock Out API: Raw response data:', responseData);
      console.log('🔍 Stock Out API: Response keys:', Object.keys(responseData));
      console.log('🔍 Stock Out API: Response type:', typeof responseData);
      console.log('🔍 Stock Out API: Is array?', Array.isArray(responseData));
      console.log('🔍 Stock Out API: Full response JSON:', JSON.stringify(responseData, null, 2));

      // Try multiple possible response structures
      let entries = [];
      if (responseData.stock_out_records) {
        entries = responseData.stock_out_records;
        console.log('✅ Found entries in stock_out_records');
      } else if (responseData.stock_out_entries) {
        entries = responseData.stock_out_entries;
        console.log('✅ Found entries in stock_out_entries');
      } else if (responseData.entries) {
        entries = responseData.entries;
        console.log('✅ Found entries in entries');
      } else if (Array.isArray(responseData)) {
        entries = responseData;
        console.log('✅ Response is direct array');
      } else if (responseData.data) {
        entries = Array.isArray(responseData.data) ? responseData.data : [];
        console.log('✅ Found entries in data');
      } else {
        console.warn('⚠️ No entries found in expected locations. Full response:', JSON.stringify(responseData, null, 2));
      }

      console.log('📊 Stock Out API: Parsed entries:', entries);
      console.log('📊 Stock Out API: Number of entries:', entries.length);
      
      // Log first entry structure if available
      if (entries.length > 0) {
        console.log('📋 Stock Out API: First entry structure:', entries[0]);
        console.log('📋 Stock Out API: First entry keys:', Object.keys(entries[0]));
        console.log('📋 Stock Out API: First entry full JSON:', JSON.stringify(entries[0], null, 2));
        
        // Check for date fields
        const dateFields = Object.keys(entries[0]).filter(key => 
          key.toLowerCase().includes('date') || 
          key.toLowerCase().includes('time') ||
          key.toLowerCase().includes('created') ||
          key.toLowerCase().includes('event')
        );
        console.log('📅 Stock Out API: Date-related fields in entry:', dateFields);
      } else {
        console.warn('⚠️ No entries returned from API. Check if entries exist for this site/month combination.');
      }

      return {
        success: true,
        data: responseData,
        entries: entries,
        summary: responseData.summary || {},
        count: responseData.summary?.total_records || responseData.count || entries.length,
        lastKey: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Stock Out API: Error in getStockOutEntries:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          entries: [],
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message: error.responseData?.message || error.message || 'Failed to fetch stock-out entries',
        entries: [],
      };
    }
  },

  /**
   * Gets a single stock-out entry by ID.
   * GET /stock/stock-out/{stock_out_id}
   * @param {string} stockOutId - Stock out entry ID
   * @returns {Promise<Object>} Object containing success status and entry data
   */
  getStockOutEntry: async (stockOutId) => {
    try {
      const endpoint = `/stock/stock-out/${stockOutId}`;
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Stock Out API: Get entry response:', responseData);

      return {
        success: true,
        data: responseData,
        entry: responseData.entry || responseData.data || responseData,
      };
    } catch (error) {
      console.error('Stock Out API: Error in getStockOutEntry:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message: error.responseData?.message || error.message || 'Failed to fetch stock-out entry',
      };
    }
  },

  /**
   * Updates a stock-out entry.
   * PUT /stock/stock-out/{ledger_id}
   * @param {string} ledgerId - Ledger entry ID (ledger_id)
   * @param {Object} updateData - Update data
   * @param {number} updateData.quantity - Quantity (required)
   * @param {string} updateData.reason - Reason: "Sold" or "Returned" (required)
   * @param {number} [updateData.selling_price] - Selling price (required when reason = "Sold")
   * @param {string} [updateData.stock_date] - Stock date (YYYY-MM-DD)
   * @param {string} [updateData.reference_id] - Reference ID
   * @param {string} [updateData.vendor_name] - Vendor name
   * @param {string} [updateData.expiry_date] - Expiry date (YYYY-MM-DD)
   * @param {string} [updateData.notes] - Notes
   * @returns {Promise<Object>} Object containing success status and response data
   */
  updateStockOutEntry: async (ledgerId, updateData) => {
    try {
      if (!ledgerId) {
        throw new Error('Ledger entry ID is required for update');
      }

      console.log('📤 Stock Out API: Updating stock-out entry');
      console.log('📤 Stock Out API: Ledger ID:', ledgerId);
      console.log('📤 Stock Out API: Update data:', JSON.stringify(updateData, null, 2));
      
      // Backend expects: PUT /stock/stock-out/{id} or PUT /stock/stock-out/{ledger_id}
      // The API Gateway route should be configured with path parameter {id} or {ledger_id}
      // Try the standard format first
      const endpoint = `/stock/stock-out/${ledgerId}`;
      console.log('📤 Stock Out API: Update endpoint:', endpoint);
      console.log('📤 Stock Out API: Full URL:', `${API_BASE_URL}${endpoint}`);
      console.log('📤 Stock Out API: Ledger ID in path:', ledgerId);
      console.log('📤 Stock Out API: Request method: PUT');
      console.log('📤 Stock Out API: Request headers: Content-Type: application/json, Authorization: Bearer <token>');
      
      const response = await makeApiRequest(endpoint, 'PUT', updateData);
      const responseData = await response.json();

      console.log('📥 Stock Out API: Update response status:', response.status);
      console.log('📥 Stock Out API: Update response data:', JSON.stringify(responseData, null, 2));

      // Check if the response indicates success
      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          data: responseData,
          entry: responseData.entry || responseData.data || responseData,
        };
      } else {
        throw new Error(`Update failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Stock Out API: Error in updateStockOutEntry:', error);
      console.error('Stock Out API: Error details:', {
        message: error.message,
        status: error.status,
        endpoint: error.endpoint,
        responseData: error.responseData,
      });
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      // If update fails, return error - DO NOT fall through to create
      // Check if it's a CORS error
      const isCorsError = !error.status && (error.message?.includes('CORS') || error.message?.includes('Failed to fetch') || error.message?.includes('preflight'));
      
      return {
        success: false,
        error: error.responseData?.error || (isCorsError ? 'CORS Error' : 'API Error'),
        message: isCorsError 
          ? 'CORS Error: Backend API needs to allow PUT requests. The API Gateway CORS configuration must include PUT method and handle OPTIONS preflight requests.'
          : (error.responseData?.message || error.message || 'Failed to update stock-out entry'),
        status: error.status,
      };
    }
  },

  /**
   * Deletes a stock-out entry.
   * DELETE /stock/stock-out/{ledger_id}
   * @param {string} ledgerId - Ledger entry ID (ledger_id)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  deleteStockOutEntry: async (ledgerId) => {
    try {
      if (!ledgerId) {
        throw new Error('Ledger entry ID is required for deletion');
      }

      console.log('🗑️ Stock Out API: Deleting stock-out entry');
      console.log('🗑️ Stock Out API: Ledger ID:', ledgerId);
      
      const endpoint = `/stock/stock-out/${ledgerId}`;
      console.log('🗑️ Stock Out API: Delete endpoint:', endpoint);
      console.log('🗑️ Stock Out API: Full URL:', `${API_BASE_URL}${endpoint}`);
      
      const response = await makeApiRequest(endpoint, 'DELETE', null);
      const responseData = await response.json();

      console.log('🗑️ Stock Out API: Delete response status:', response.status);
      console.log('🗑️ Stock Out API: Delete response data:', JSON.stringify(responseData, null, 2));

      // Check if the response indicates success
      if (response.status >= 200 && response.status < 300) {
        return {
          success: true,
          data: responseData,
          message: responseData.message || 'Stock-out entry deleted successfully',
          ledger_id: responseData.ledger_id || ledgerId,
        };
      } else {
        throw new Error(`Delete failed with status ${response.status}`);
      }
    } catch (error) {
      console.error('Stock Out API: Error in deleteStockOutEntry:', error);
      console.error('Stock Out API: Error details:', {
        message: error.message,
        status: error.status,
        endpoint: error.endpoint,
        responseData: error.responseData,
      });
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message: error.responseData?.message || error.message || 'Failed to delete stock-out entry',
        status: error.status,
      };
    }
  },

  /**
   * Fetches available sites.
   * GET /site/getall
   * @returns {Promise<Array>} Array of site objects
   */
  getSites: async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error(
          'Authentication token is missing. Please ensure a user is logged in.'
        );
      }

      console.log(`Stock Out API: Calling endpoint: ${API_BASE_URL}/site/getall`);

      const response = await fetch(`${API_BASE_URL}/site/getall`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.log(`Stock Out API: HTTP Error: ${response.status} ${response.statusText}`);
        console.log(`Stock Out API: Response Body:`, responseText);

        let errorMessage;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage =
            errorData.message ||
            errorData.error ||
            `HTTP error! status: ${response.status}`;
        } catch {
          errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Stock Out API: Sites fetched successfully:', result);
      
      // Handle different response formats
      if (Array.isArray(result)) {
        return result;
      } else if (result.sites && Array.isArray(result.sites)) {
        return result.sites;
      } else if (result.data && Array.isArray(result.data)) {
        return result.data;
      }
      
      return result;
    } catch (error) {
      console.error('Stock Out API: Error fetching sites:', error);
      // Return empty array on error to prevent form from breaking
      return [];
    }
  },
};

